import { effectiveParentId, type LiveProject } from '@wiztime/core';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { settingsPath, SettingsSection } from '../../../app/routes';
import type { ProjectsView } from '../../../hooks/useProjects';
import { AnchoredFormPopover } from '../../../ui/AddPopover';
import type { MenuItem } from '../../../ui/MenuButton';
import { PointMenu } from '../../../ui/PointMenu';
import type { ProjectsLinkState } from '../../settings/sections/ProjectEditForm';
import { ProjectNameForm } from '../project/ProjectNameForm';
import type { useCreateQuickProject } from './useCreateQuickProject';

/** What the card currently shows next to a tile: its menu or the rename form. */
export const TileUiKind = { Menu: 'menu', Rename: 'rename' } as const;
export type TileUi =
  | { readonly kind: typeof TileUiKind.Menu; readonly project: LiveProject; readonly anchor: HTMLElement; readonly x: number; readonly y: number }
  | { readonly kind: typeof TileUiKind.Rename; readonly project: LiveProject; readonly anchor: HTMLElement };

/** What the host needs from the card: the project list, the writes and the callbacks that open the forms. */
interface TileUiHostProps {
  readonly tileUi: TileUi | null;
  readonly projects: ProjectsView;
  readonly creator: ReturnType<typeof useCreateQuickProject>;
  readonly onClose: () => void;
  readonly onRename: (project: LiveProject, anchor: HTMLElement) => void;
  /** Opens the title row's "+" popover with `parent` preselected. */
  readonly onAddChild: (parent: LiveProject) => void;
}

/** The tile menu and the rename form popover of the quick-mark card. */
export function TileUiHost({ tileUi, projects, creator, onClose, onRename, onAddChild }: TileUiHostProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const byId = projects.byId;

  const menuItems = (project: LiveProject, anchor: HTMLElement): readonly MenuItem[] => {
    // Only a project that is top-level in the stored data can get children; one whose stored parent
    // is a live project (damaged data: more than two levels) cannot.
    const storedParent = project.data.parentId;
    const hasLiveParent = typeof storedParent === 'string' && storedParent !== '' && byId.has(storedParent);
    const canAddChild = effectiveParentId(project, byId) === null && !hasLiveParent;
    return [
      ...(canAddChild
        ? [
            {
              id: 'add-child',
              label: t('quickMark.menuAddChild'),
              testId: 'quick-mark-menu-add-child',
              onSelect: () => onAddChild(project),
            },
          ]
        : []),
      {
        id: 'rename',
        label: t('quickMark.menuRename'),
        testId: 'quick-mark-menu-rename',
        onSelect: () => onRename(project, anchor),
      },
      {
        id: 'edit-in-settings',
        label: t('quickMark.menuEditInSettings'),
        testId: 'quick-mark-menu-settings',
        onSelect: () => {
          const state: ProjectsLinkState = { editProjectId: project.id };
          void navigate(settingsPath(SettingsSection.Projects), { state });
        },
      },
    ];
  };

  if (tileUi === null) return null;
  switch (tileUi.kind) {
    case TileUiKind.Menu:
      return (
        <PointMenu
          label={t('quickMark.menuLabel', { name: tileUi.project.data.name })}
          items={menuItems(tileUi.project, tileUi.anchor)}
          x={tileUi.x}
          y={tileUi.y}
          testId="quick-mark-menu"
          onClose={(restoreFocus) => {
            onClose();
            if (restoreFocus && tileUi.anchor.isConnected) tileUi.anchor.focus({ preventScroll: true });
          }}
        />
      );
    case TileUiKind.Rename:
      return (
        <AnchoredFormPopover
          label={t('quickMark.renameLabel', { name: tileUi.project.data.name })}
          testId="quick-mark-rename"
          anchor={tileUi.anchor}
          onClose={onClose}
        >
          {(close) => (
            <ProjectNameForm
              initialName={tileUi.project.data.name}
              otherNames={projects.all.filter((entry) => entry.id !== tileUi.project.id).map((entry) => entry.data.name)}
              submitLabel={t('quickMark.renameSave')}
              testIdPrefix="quick-mark-rename"
              onSubmit={(name) => creator.rename(tileUi.project, name)}
              onDone={close}
            />
          )}
        </AnchoredFormPopover>
      );
  }
}
